/**
 * SellerCreditsForm - Step 5 of CD Creation Wizard
 *
 * Manages seller credits, prorations, and other adjustments:
 * - Deposit/Earnest Money
 * - Seller Concessions
 * - Property Tax Prorations
 * - HOA Prorations
 * - Other Adjustments (dynamic)
 */

import { Plus, Trash2 } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  selectServerLineItems,
  useClosingDisclosureStore,
} from '../../store/closingDisclosureStore';
import type { LineItem } from '../../types';
import { CurrencyInput, formatCurrency } from './shared';

/** Adjustment credit type */
type CreditTo = 'borrower' | 'seller';

/** Custom adjustment entry */
interface Adjustment {
  id: string;
  description: string;
  amount: number | null;
  creditTo: CreditTo;
}

/** Form data for seller credits step */
export interface SellerCreditsData {
  depositAmount: number | null;
  depositHeldBy: string;
  sellerConcessions: number | null;
  sellerConcessionsDescription: string;
  propertyTaxProration: number | null;
  propertyTaxProrationPeriod: string;
  hoaProration: number | null;
  hoaProrationPeriod: string;
  adjustments: Adjustment[];
}

interface SellerCreditsFormProps {
  data?: Partial<SellerCreditsData>;
  lineItems?: LineItem[];
  onNext: (data: SellerCreditsData) => void;
  onBack: () => void;
}

/** Default form data */
function getDefaultData(): SellerCreditsData {
  return {
    depositAmount: null,
    depositHeldBy: '',
    sellerConcessions: null,
    sellerConcessionsDescription: '',
    propertyTaxProration: null,
    propertyTaxProrationPeriod: '',
    hoaProration: null,
    hoaProrationPeriod: '',
    adjustments: [],
  };
}

/** Generate unique ID for adjustments */
function generateId(): string {
  return `adj-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Reconstruct SellerCreditsData from section L line items
 * This is used when navigating back to this step to restore previously entered data
 */
function reconstructFromLineItems(lineItems?: LineItem[]): Partial<SellerCreditsData> {
  if (!lineItems || lineItems.length === 0) return {};

  const sectionLItems = lineItems.filter((item) => item.section === 'L');
  if (sectionLItems.length === 0) return {};

  const data: Partial<SellerCreditsData> = {
    adjustments: [],
  };

  for (const item of sectionLItems) {
    const desc = item.description?.toLowerCase() || '';
    const lineNum = item.lineNumberString || '';

    // Deposit/Earnest Money (L01)
    if (desc.includes('deposit') || desc.includes('earnest money') || lineNum === 'L01') {
      // Use ?? instead of || to preserve 0 values
      data.depositAmount = item.borrowerPaidAtClosing ?? null;
      // Extract "held by" from description if present
      const heldByMatch = item.description?.match(/\(held by ([^)]+)\)/i);
      if (heldByMatch) {
        data.depositHeldBy = heldByMatch[1];
      }
    }
    // Seller Concessions (L02)
    else if (desc.includes('seller concession') || lineNum === 'L02') {
      // Use ?? instead of || to preserve 0 values
      data.sellerConcessions = item.sellerPaidAtClosing ?? null;
      data.sellerConcessionsDescription =
        item.description?.replace(/seller concessions?/i, '').trim() || '';
    }
    // Property Tax Proration (L03)
    else if (desc.includes('property tax proration') || lineNum === 'L03') {
      // Use ?? instead of || to preserve 0 values
      data.propertyTaxProration = item.sellerPaidAtClosing ?? null;
      // Extract period from description if present
      const periodMatch = item.description?.match(/\(([^)]+)\)/);
      if (periodMatch) {
        data.propertyTaxProrationPeriod = periodMatch[1];
      }
    }
    // HOA Proration (L04)
    else if (desc.includes('hoa proration') || lineNum === 'L04') {
      // Use ?? instead of || to preserve 0 values
      data.hoaProration = item.sellerPaidAtClosing ?? null;
      // Extract period from description if present
      const periodMatch = item.description?.match(/\(([^)]+)\)/);
      if (periodMatch) {
        data.hoaProrationPeriod = periodMatch[1];
      }
    }
    // Other adjustments (L05+)
    else {
      const amount = (item.borrowerPaidAtClosing || 0) + (item.sellerPaidAtClosing || 0);
      if (amount > 0) {
        data.adjustments = data.adjustments || [];
        data.adjustments.push({
          id: item.id || generateId(),
          description: item.description || 'Adjustment',
          amount,
          creditTo: (item.sellerPaidAtClosing || 0) > 0 ? 'seller' : 'borrower',
        });
      }
    }
  }

  return data;
}

const SellerCreditsForm: React.FC<SellerCreditsFormProps> = ({
  data: initialData,
  lineItems: _lineItems,
  onNext,
  onBack,
}) => {
  // Read directly from store to ensure we always have the latest data
  // This bypasses any prop-passing timing issues
  const storeLineItems = useClosingDisclosureStore(selectServerLineItems);

  // Reconstruct data from store line items (memoized to avoid recalculating on every render)
  const reconstructedData = useMemo(() => {
    return reconstructFromLineItems(storeLineItems);
  }, [storeLineItems]);

  // Note: With key={`step-${currentStep}`} on the wizard, this component remounts on step change
  // We use the store data directly as the source of truth
  const [formData, setFormData] = useState<SellerCreditsData>({
    ...getDefaultData(),
    ...reconstructedData,
    ...initialData,
  });

  // Keep local state in sync with store
  // This ensures we always reflect the latest saved data
  useEffect(() => {
    const sectionLItems = storeLineItems?.filter((item) => item.section === 'L') || [];
    // Always sync from store when it has section L data
    // The store is the source of truth for persisted data
    if (sectionLItems.length > 0) {
      const reconstructed = reconstructFromLineItems(storeLineItems);
      setFormData((prev) => ({
        ...prev,
        ...reconstructed,
      }));
    }
  }, [storeLineItems]);

  // Update a single field
  const updateField = useCallback(
    <K extends keyof SellerCreditsData>(field: K, value: SellerCreditsData[K]) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    [],
  );

  // Add a new adjustment
  const addAdjustment = useCallback(() => {
    const newAdjustment: Adjustment = {
      id: generateId(),
      description: '',
      amount: null,
      creditTo: 'borrower',
    };
    setFormData((prev) => ({
      ...prev,
      adjustments: [...prev.adjustments, newAdjustment],
    }));
  }, []);

  // Update an adjustment
  const updateAdjustment = useCallback((id: string, updates: Partial<Adjustment>) => {
    setFormData((prev) => ({
      ...prev,
      adjustments: prev.adjustments.map((adj) => (adj.id === id ? { ...adj, ...updates } : adj)),
    }));
  }, []);

  // Remove an adjustment
  const removeAdjustment = useCallback((id: string) => {
    setFormData((prev) => ({
      ...prev,
      adjustments: prev.adjustments.filter((adj) => adj.id !== id),
    }));
  }, []);

  // Calculate totals
  const totals = useMemo(() => {
    const borrowerCredits =
      (formData.depositAmount ?? 0) +
      (formData.sellerConcessions ?? 0) +
      formData.adjustments
        .filter((adj) => adj.creditTo === 'borrower')
        .reduce((sum, adj) => sum + (adj.amount ?? 0), 0);

    const sellerCredits =
      (formData.propertyTaxProration ?? 0) +
      (formData.hoaProration ?? 0) +
      formData.adjustments
        .filter((adj) => adj.creditTo === 'seller')
        .reduce((sum, adj) => sum + (adj.amount ?? 0), 0);

    return {
      borrowerCredits,
      sellerCredits,
      netToSeller: sellerCredits - borrowerCredits,
    };
  }, [formData]);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Instructions */}
      <div className="bg-primary/10 border border-primary/20 text-primary px-4 py-3 rounded">
        <p className="text-sm">
          <strong>Optional Step:</strong> Enter any credits from the seller, prorations, or other
          adjustments that apply to this transaction. These affect the final cash to close.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form - Left 2/3 */}
        <div className="lg:col-span-2 space-y-6">
          {/* Deposit / Earnest Money */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Deposit / Earnest Money</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="depositAmount">Deposit Amount</Label>
                  <CurrencyInput
                    id="depositAmount"
                    value={formData.depositAmount}
                    onChange={(value) => updateField('depositAmount', value)}
                    placeholder="$0.00"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Credited to borrower at closing
                  </p>
                </div>
                <div>
                  <Label htmlFor="depositHeldBy">Deposit Held By</Label>
                  <Input
                    id="depositHeldBy"
                    value={formData.depositHeldBy}
                    onChange={(e) => updateField('depositHeldBy', e.target.value)}
                    placeholder="e.g., ABC Title Company"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Seller Concessions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Seller Concessions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="sellerConcessions">Concession Amount</Label>
                  <CurrencyInput
                    id="sellerConcessions"
                    value={formData.sellerConcessions}
                    onChange={(value) => updateField('sellerConcessions', value)}
                    placeholder="$0.00"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Seller credit toward borrower closing costs
                  </p>
                </div>
                <div>
                  <Label htmlFor="sellerConcessionsDescription">Description</Label>
                  <Input
                    id="sellerConcessionsDescription"
                    value={formData.sellerConcessionsDescription}
                    onChange={(e) => updateField('sellerConcessionsDescription', e.target.value)}
                    placeholder="e.g., Closing cost credit per contract"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Prorations */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Prorations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Property Tax Proration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="propertyTaxProration">Property Tax Proration</Label>
                  <CurrencyInput
                    id="propertyTaxProration"
                    value={formData.propertyTaxProration}
                    onChange={(value) => updateField('propertyTaxProration', value)}
                    placeholder="$0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="propertyTaxProrationPeriod">Proration Period</Label>
                  <Input
                    id="propertyTaxProrationPeriod"
                    value={formData.propertyTaxProrationPeriod}
                    onChange={(e) => updateField('propertyTaxProrationPeriod', e.target.value)}
                    placeholder="e.g., 1/1/2024 - 6/15/2024"
                  />
                </div>
              </div>

              {/* HOA Proration */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="hoaProration">HOA Dues Proration</Label>
                  <CurrencyInput
                    id="hoaProration"
                    value={formData.hoaProration}
                    onChange={(value) => updateField('hoaProration', value)}
                    placeholder="$0.00"
                  />
                </div>
                <div>
                  <Label htmlFor="hoaProrationPeriod">Proration Period</Label>
                  <Input
                    id="hoaProrationPeriod"
                    value={formData.hoaProrationPeriod}
                    onChange={(e) => updateField('hoaProrationPeriod', e.target.value)}
                    placeholder="e.g., June 2024"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Prorations are typically credited to the seller for the portion of the period after
                closing that they will not occupy the property.
              </p>
            </CardContent>
          </Card>

          {/* Other Adjustments */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Other Adjustments</CardTitle>
              <Button type="button" variant="outline" size="sm" onClick={addAdjustment}>
                <Plus className="h-4 w-4 mr-1" />
                Add Adjustment
              </Button>
            </CardHeader>
            <CardContent>
              {formData.adjustments.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No additional adjustments. Click "Add Adjustment" to add custom credits or debits.
                </p>
              ) : (
                <div className="space-y-4">
                  {formData.adjustments.map((adjustment, index) => (
                    <div
                      key={adjustment.id}
                      className="grid grid-cols-12 gap-2 items-end border-b pb-4 last:border-b-0"
                    >
                      <div className="col-span-5">
                        <Label htmlFor={`adj-desc-${index}`}>Description</Label>
                        <Input
                          id={`adj-desc-${index}`}
                          value={adjustment.description}
                          onChange={(e) =>
                            updateAdjustment(adjustment.id, { description: e.target.value })
                          }
                          placeholder="e.g., Repair credit"
                        />
                      </div>
                      <div className="col-span-3">
                        <Label htmlFor={`adj-amount-${index}`}>Amount</Label>
                        <CurrencyInput
                          id={`adj-amount-${index}`}
                          value={adjustment.amount}
                          onChange={(value) => updateAdjustment(adjustment.id, { amount: value })}
                          placeholder="$0.00"
                        />
                      </div>
                      <div className="col-span-3">
                        <Label htmlFor={`adj-credit-${index}`}>Credit To</Label>
                        <select
                          id={`adj-credit-${index}`}
                          value={adjustment.creditTo}
                          onChange={(e) =>
                            updateAdjustment(adjustment.id, {
                              creditTo: e.target.value as CreditTo,
                            })
                          }
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                        >
                          <option value="borrower">Borrower</option>
                          <option value="seller">Seller</option>
                        </select>
                      </div>
                      <div className="col-span-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeAdjustment(adjustment.id)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Summary Sidebar - Right 1/3 */}
        <div className="lg:col-span-1">
          <Card className="bg-muted sticky top-6">
            <CardHeader>
              <CardTitle className="text-lg">Credits Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Credits to Borrower */}
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">
                  Credits to Borrower
                </p>
                {formData.depositAmount !== null && formData.depositAmount !== undefined ? (
                  <div className="flex justify-between text-sm">
                    <span>Deposit/Earnest Money</span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      {formatCurrency(formData.depositAmount)}
                    </span>
                  </div>
                ) : null}
                {formData.sellerConcessions !== null && formData.sellerConcessions !== undefined ? (
                  <div className="flex justify-between text-sm">
                    <span>Seller Concessions</span>
                    <span className="font-medium text-green-600 dark:text-green-400">
                      {formatCurrency(formData.sellerConcessions)}
                    </span>
                  </div>
                ) : null}
                {formData.adjustments
                  .filter((adj) => adj.creditTo === 'borrower' && adj.amount)
                  .map((adj) => (
                    <div key={adj.id} className="flex justify-between text-sm">
                      <span className="truncate pr-2">{adj.description || 'Adjustment'}</span>
                      <span className="font-medium text-green-600 dark:text-green-400">
                        {formatCurrency(adj.amount ?? 0)}
                      </span>
                    </div>
                  ))}
                {totals.borrowerCredits === 0 && (
                  <p className="text-sm text-muted-foreground italic">No borrower credits</p>
                )}
                <div className="flex justify-between font-semibold border-t pt-2">
                  <span>Total Borrower Credits</span>
                  <span className="text-green-600 dark:text-green-400">
                    {formatCurrency(totals.borrowerCredits)}
                  </span>
                </div>
              </div>

              {/* Credits to Seller */}
              <div className="space-y-2 border-t pt-4">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">
                  Credits to Seller
                </p>
                {formData.propertyTaxProration ? (
                  <div className="flex justify-between text-sm">
                    <span>Property Tax Proration</span>
                    <span className="font-medium">
                      {formatCurrency(formData.propertyTaxProration)}
                    </span>
                  </div>
                ) : null}
                {formData.hoaProration ? (
                  <div className="flex justify-between text-sm">
                    <span>HOA Proration</span>
                    <span className="font-medium">{formatCurrency(formData.hoaProration)}</span>
                  </div>
                ) : null}
                {formData.adjustments
                  .filter((adj) => adj.creditTo === 'seller' && adj.amount)
                  .map((adj) => (
                    <div key={adj.id} className="flex justify-between text-sm">
                      <span className="truncate pr-2">{adj.description || 'Adjustment'}</span>
                      <span className="font-medium">{formatCurrency(adj.amount ?? 0)}</span>
                    </div>
                  ))}
                {totals.sellerCredits === 0 && (
                  <p className="text-sm text-muted-foreground italic">No seller credits</p>
                )}
                <div className="flex justify-between font-semibold border-t pt-2">
                  <span>Total Seller Credits</span>
                  <span>{formatCurrency(totals.sellerCredits)}</span>
                </div>
              </div>

              {/* Net Effect */}
              <div className="border-t pt-4">
                <div className="flex justify-between text-lg">
                  <span className="font-bold">Net to Seller</span>
                  <span
                    className={`font-bold ${
                      totals.netToSeller >= 0
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {formatCurrency(Math.abs(totals.netToSeller))}
                    {totals.netToSeller < 0 && ' (Credit)'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {totals.netToSeller >= 0
                    ? 'Seller receives this amount at closing'
                    : 'Seller owes this amount at closing'}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-6 border-t">
        <Button type="button" variant="outline" onClick={onBack}>
          Back
        </Button>
        <div className="flex gap-2">
          <Button type="button" variant="ghost" onClick={() => onNext(getDefaultData())}>
            Skip (No Credits)
          </Button>
          <Button type="submit">Next: Review & Generate</Button>
        </div>
      </div>
    </form>
  );
};

export default SellerCreditsForm;
